import { debounceTime, map, } from 'rxjs';
import { FormControl } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { Injectable, Signal, WritableSignal, computed, signal } from '@angular/core';

/**
 * interfaces
 */
import { ITreeNode } from '../interfaces';

/**
 * enums
 */
import { ITreeSelectionMode, TreeSelectionMode } from '../enums';

/**
 * utilities
 */
import { filterTree } from '../utilities';

/**
 * @name TreeStateService
 * @description
 * Signal-based state for a single {@link TreeComponent} instance.
 * Provided at component scope (not root) so each tree has isolated state.
 *
 * @remarks
 * Step 1 provides placeholders only; selection, filter, and keyboard state arrive in later steps.
 */
@Injectable()
export class TreeStateService {

  /**
   * @description Root nodes mirrored from the host `value` input (wired in Step 2+).
   */
  private readonly _value: WritableSignal<ITreeNode[]> = signal<ITreeNode[]>([]);

  /**
   * @description Read-only view of the current root node list.
   */
  public readonly value: Signal<ITreeNode[]> = this._value.asReadonly();

  /**
   * @description Count of root nodes for lightweight diagnostics.
   */
  public readonly nodeCount: Signal<number> = computed(() => this.value().length); 

  /**
   * @description Reactive form control bound to the filter input field.
   */
  public readonly filterFormControl: FormControl<string | null> = new FormControl<string | null>('')

  /** 
   * @description Signal to hold the field name to filter by
   */
  private readonly _filterBy: WritableSignal<string> = signal<string>('label');

  /**
   * @description Read-only access to the current filter-by property.
   */
  public readonly filterBy: Signal<string> = this._filterBy.asReadonly()

  /**
   * @description Debounced signal that tracks the sanitized value of the filter input.
   */
  public readonly filterValue: Signal<string | undefined> = toSignal(
    this.filterFormControl.valueChanges.pipe(
      map<string | null, string>(x => String(x).toLowerCase().trim()),
      debounceTime(500),
    )
  )

  /**
   * @description Read-only filtered nodes exposed to the host template.
   */
  public readonly filteredNodes = computed(() => {

    // init
    const _value = this.value();
    const _filterValue = this.filterValue()
    const _filterBy = this.filterBy() as keyof ITreeNode;
    

    // filter by value
    if (_filterValue && _filterValue.length > 0) {
      return filterTree(_value, _filterValue, _filterBy)
    }
    
    return _value
  })

  /**
   *
   */
  constructor() {}

  /**
   * @description Replaces the internal root list (called from `TreeComponent` when value changes).
   * @param nodes - New root nodes.
   * @returns void
   */
  public setValue(nodes: ITreeNode[]): void {
    
    // validate nodes
    if (!Array.isArray(nodes)) {
      throw new Error('Nodes must be an array');
    }

    // set value
    this._value.set(nodes);
  }

  /**
   * @description Configures the target field path for the filtering algorithm.
   * @param value String representing the field path (e.g. 'label').
   */
  public setFilterBy(value: string) {

    // validate nodes
    if (typeof value !== 'string') {
      throw new Error('value must be an string');
    }

    // set filterBy
    this._filterBy.set(value)

  }

  /**
   * @description Clears filter state and restores `filteredNodes` to the full value list.
   * @returns void
   * @remarks Full filter pipeline is implemented in Step 6.
   */
  public resetFilter(): void {
    
    // void
    if (this.filterValue() === '') return;

    this.filterFormControl.reset('')
  }

  /**
   * @description Expands every expandable node in the tree.
   */
  public expandAll(): void {
    const updatedNodes = this.toggleAllNodes(this._value(), true);
    this.setValue(updatedNodes);
  }

  /**
   * @description Collapses every expanded node in the tree.
   */
  public collapseAll(): void {
    const updatedNodes = this.toggleAllNodes(this._value(), false);
    this.setValue(updatedNodes);
  }

  /**
   * @description Recursively changes the expanded state of nodes using Immutable Updates.
   */
  private toggleAllNodes(nodes: ITreeNode[], expanded: boolean): ITreeNode[] {
    return nodes.map(node => {

      // init new ref
      const updatedNode = { ...node, expanded };

      // Recursive
      if (!updatedNode.leaf && updatedNode.children && updatedNode.children.length > 0) {
        updatedNode.children = this.toggleAllNodes(updatedNode.children, expanded);
      }

      return updatedNode;

    });
  }

  /**
   * @description Checks if a specific node is currently selected.
   */
  public isSelected(node: ITreeNode, currentSelection: ITreeNode | ITreeNode[] | null): boolean {

    // void
    if (!currentSelection) return false;

    if (Array.isArray(currentSelection)) {
      return currentSelection.some(n => n.key === node.key);
    } else {
      return currentSelection.key === node.key;
    }

  }

  /**
   * @method toggleSelection
   * @description Manages complex selection logic including single, multiple, and meta-key selection modes.
   * @returns An object containing the new selection state and a boolean indicating if the node is currently selected.
   */
  public toggleSelection(
    node: ITreeNode, 
    currentSelection: ITreeNode | ITreeNode[] | null, 
    mode: ITreeSelectionMode, 
    metaKeySelection: boolean, 
    isMetaKeyPressed: boolean
  ): { newSelection: ITreeNode | ITreeNode[] | null, isSelectedNow: boolean } {
    
    // prevent selecting non-selectable nodes
    if (node.selectable === false) {
      return { newSelection: currentSelection, isSelectedNow: false };
    }

    // init
    let isSelectedNow = false;
    let newSelection: ITreeNode | ITreeNode[] | null = null;

    // single Selection Mode
    if (mode !== TreeSelectionMode.Multiple) {
      const currentlySelected = this.isSelected(node, currentSelection);
      if (currentlySelected) {
        newSelection = null; // Unselect if already selected
        isSelectedNow = false;
      } else {
        newSelection = node; // Select the new node
        isSelectedNow = true;
      }
    }
    // multiple Selection Mode
    else if (mode === TreeSelectionMode.Multiple) {
      let selectionArray = Array.isArray(currentSelection) ? [...currentSelection] : [];
      const index = selectionArray.findIndex(n => n.key === node.key);

      if (index !== -1) {
        // Node is already selected -> Remove it
        selectionArray.splice(index, 1);
        isSelectedNow = false;
      } else {
        // Node is NOT selected -> Add it
        if (metaKeySelection && !isMetaKeyPressed) {
          // If metaKey is required but NOT pressed, clear previous selections
          selectionArray = [node];
        } else {
          // Otherwise, append to existing selections
          selectionArray.push(node);
        }
        isSelectedNow = true;
      }
      newSelection = selectionArray.length > 0 ? selectionArray : null;
    }

    return { newSelection, isSelectedNow };

  }

}
